import { z } from "zod";

import type { AvailabilityDay, ClientEvent } from "@/features/booking/types/client.types";
import { addDaysToKey, dayKeyIn } from "@/features/booking/utils/client-format";

/**
 * Asking for a time: which day, what time of day, and whether it has to book right away. They live
 * in the URL, so a filtered view can be linked and survives a reload. Specialists and events both
 * read them; the backend finds who has such a time, and a card narrows its own times here.
 */

/** "Any day" is the absence of a value. */
export const WHEN_OPTIONS = ["today", "tomorrow", "week", "date"] as const;
export type When = (typeof WHEN_OPTIONS)[number];

export const DAY_PARTS = ["morning", "afternoon", "evening"] as const;
export type DayPart = (typeof DAY_PARTS)[number];

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

/** What a page accepts in its search. Anything malformed is dropped, never an error page. */
export const timeFiltersSearchSchema = z.object({
  when: z.enum(WHEN_OPTIONS).optional().catch(undefined),
  date: z.string().regex(DAY_KEY).optional().catch(undefined),
  parts: z.array(z.enum(DAY_PARTS)).max(DAY_PARTS.length).optional().catch(undefined),
  instant: z.boolean().optional().catch(undefined),
});
export type TimeFiltersSearch = z.infer<typeof timeFiltersSearchSchema>;

export interface TimeFilters {
  when: When | null;
  /** The day picked, as "2026-10-12". Only read while `when` is `date`. */
  date: string | null;
  /** Empty means any time of day. */
  parts: DayPart[];
  /** Only times that book right away. */
  instant: boolean;
}

export const NO_TIME_FILTERS: TimeFilters = { when: null, date: null, parts: [], instant: false };

/** Search params into filters. "Pick a date" without a date is no day filter at all. */
export const timeFiltersOf = (search: TimeFiltersSearch): TimeFilters => {
  const when = search.when === "date" && !search.date ? null : (search.when ?? null);
  return {
    when,
    date: when === "date" ? (search.date ?? null) : null,
    // In the order the day runs, whatever order they were ticked in, and each one once.
    parts: DAY_PARTS.filter((part) => search.parts?.includes(part)),
    instant: search.instant === true,
  };
};

/** Filters back into search params, leaving every default out so a clean view has a clean URL. */
export const searchOfTimeFilters = (filters: TimeFilters): TimeFiltersSearch => ({
  ...(filters.when && (filters.when !== "date" || filters.date) ? { when: filters.when } : {}),
  ...(filters.when === "date" && filters.date ? { date: filters.date } : {}),
  ...(filters.parts.length > 0 && filters.parts.length < DAY_PARTS.length ? { parts: filters.parts } : {}),
  ...(filters.instant ? { instant: true } : {}),
});

/** Every time of day ticked is the same as none ticked. */
export const activeParts = (filters: TimeFilters): DayPart[] =>
  filters.parts.length === DAY_PARTS.length ? [] : filters.parts;

/** Whether a time is being asked for at all. */
export const asksForTime = (filters: TimeFilters) =>
  filters.when !== null || activeParts(filters).length > 0 || filters.instant;

/**
 * "Next 7 days" counts from today. A calendar week would leave a Sunday evening with nothing to
 * show, which is not what someone asking "who is free this week" means.
 */
export const WEEK_DAYS = 7;

export interface DayWindow {
  /** First day the grid asks for. Undefined is "today at the business". */
  from: string | undefined;
  /**
   * How many days, from the first one, a specialist must have a time in to be listed. Null when
   * no day is picked: everyone is listed, with or without times on this page.
   */
  mustHaveWithin: number | null;
}

/** The day filter as the request and the list need it. */
export const dayWindow = (filters: TimeFilters, todayKey: string): DayWindow => {
  switch (filters.when) {
    case "today":
      return { from: undefined, mustHaveWithin: 1 };
    case "tomorrow":
      return { from: addDaysToKey(todayKey, 1), mustHaveWithin: 1 };
    case "week":
      return { from: undefined, mustHaveWithin: WEEK_DAYS };
    case "date":
      // A day already gone has nothing to book; the grid starts today instead.
      return filters.date && filters.date >= todayKey
        ? { from: filters.date === todayKey ? undefined : filters.date, mustHaveWithin: 1 }
        : { from: undefined, mustHaveWithin: null };
    default:
      return { from: undefined, mustHaveWithin: null };
  }
};

/** Morning is before 12, afternoon is 12 to 5, evening is after 5, on the specialist's clock. */
export const dayPartOf = (startAt: string, timeZone: string): DayPart => {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", { hour: "2-digit", hourCycle: "h23", timeZone }).format(new Date(startAt)),
  );
  if (hour < 12) return "morning";
  return hour < 17 ? "afternoon" : "evening";
};

/** One day of times as public availability answers it. Nothing taken is ever in it. */
type PublicDay = Pick<AvailabilityDay, "slots">;

/**
 * A page of times with the filters applied: open times outside the picked parts of the day go, and
 * so do times that need a yes when only instant ones are wanted.
 */
export const filterDays = <Day extends PublicDay>(days: Day[], filters: TimeFilters, timeZone: string): Day[] => {
  const parts = activeParts(filters);
  if (parts.length === 0 && !filters.instant) return days;
  const inParts = (startAt: string) => parts.length === 0 || parts.includes(dayPartOf(startAt, timeZone));
  return days.map((day) => ({
    ...day,
    slots: day.slots.filter((slot) => inParts(slot.startAt) && (!filters.instant || slot.kind === "instant")),
  }));
};

/** Events inside the day window. Only the day filter applies to events. */
export const eventMatchesWhen = (
  event: Pick<ClientEvent, "startAt" | "timeZone">,
  filters: TimeFilters,
  now = new Date(),
): boolean => {
  if (filters.when === null) return true;
  const todayKey = dayKeyIn(now, event.timeZone);
  const eventKey = dayKeyIn(event.startAt, event.timeZone);
  switch (filters.when) {
    case "today":
      return eventKey === todayKey;
    case "tomorrow":
      return eventKey === addDaysToKey(todayKey, 1);
    case "week":
      return eventKey >= todayKey && eventKey < addDaysToKey(todayKey, WEEK_DAYS);
    default:
      return filters.date ? eventKey === filters.date : true;
  }
};

/** "2026-10-12" as a local Date for the calendar, and back. */
export const dateOfKey = (key: string): Date => {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, (month ?? 1) - 1, day ?? 1);
};

export const keyOfDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
